import { clamp, cloneDeep, isArray, isEqual } from "lodash"
import React, { ReactNode, useRef, useState } from "react"
import { Keyboard, Platform, Pressable, StyleSheet } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { useScreenOrientation, useTransitionEffect } from "@axtesys/hooks"
import {
  Box,
  ButtonBar,
  Card,
  Column,
  Gap,
  Icon,
  Label,
  MCIcon,
  MobileSearchBar,
  Overlay,
  Row,
  RowSeparator,
  ToolTipIcon,
} from "@axtesys/kassen-app-ui"
import {
  contains,
  createCustomContext,
  createId,
  filterItems,
  indexOf,
  isEmptyOrBlank,
  notIn,
} from "@axtesys/react-tools"

import { useGeneralTranslation } from "../../feature/Translation/hooks"
import { PerformanceList } from "../PerformanceList"

type SharedSelectionConfig<T> = {
  key: string

  items: T[]
  displayMapping: (item: T) => ReactNode | string

  title?: string
  tooltip?: string
  itemKey?: keyof T
  preventCancel?: boolean
  filterSelector?: (item: T) => Array<string | undefined>
}
type SingleSelectionConfig<T> = SharedSelectionConfig<T> & {
  selection?: T
  mustConfirm?: boolean
}
type MultiSelectionConfig<T> = SharedSelectionConfig<T> & { selection?: T[] }
type AggregatedSelectionConfig<T> =
  | (SingleSelectionConfig<T> & { resolve?: (item: T) => void })
  | (MultiSelectionConfig<T> & { resolve?: (items: T[]) => void })
  | undefined

export { SelectionProvider, useSelection, useUpdateSelectionConfigEffect }

function SelectionProvider({ children }: { children: ReactNode }) {
  return (
    <SelectionContextProvider>
      {children}
      <SelectionOverlay />
    </SelectionContextProvider>
  )
}

function SelectionOverlay() {
  const {
    singleSelectionInProgress,
    selectionConfig,
    searchString,
    setSearchString,
  } = useSelectionInternal()
  const { top } = useSafeAreaInsets()
  const orientation = useScreenOrientation()
  const { tGeneral } = useGeneralTranslation()

  if (!selectionConfig) return null

  const { title, items, itemKey, tooltip, selection, filterSelector } =
    selectionConfig

  const isWeb = Platform.OS == "web"
  const cardStyle = [
    styles.cardOuterSpacing,
    isWeb && styles.webCard,
    { marginTop: clamp(top, 24, Number.MAX_VALUE) },
    !isWeb && orientation == "landscape" && styles.tabletCard,
  ]

  const showFilter = filterSelector != undefined
  const itemsToShow = showFilter
    ? filterItems(items, searchString, filterSelector)
    : items
  const itemsPresent = itemsToShow.length > 0

  const heading = title && <Label h4 text={title} />
  const headingComponent = isEmptyOrBlank(tooltip) ? (
    heading
  ) : (
    <Row alignCenter gap="XXS">
      {heading}
      <ToolTipIcon text={tooltip!} />
    </Row>
  )

  // In case the selection context is in multi selection mode,
  // we do want to display the aggregate selection button (select/deselect all).
  const titleComponent = !singleSelectionInProgress ? (
    <Row expand alignCenter spaceBetween>
      {headingComponent}
      {itemsPresent ? (
        <AggregateSelectionIcon
          selection={selection}
          itemsToShow={itemsToShow}
        />
      ) : (
        <Gap size="S" />
      )}
    </Row>
  ) : (
    headingComponent
  )

  const emptyListText = (
    <Column gap="XS">
      <RowSeparator />
      <Box padLeft="XS">
        <Label
          bold
          color="medium"
          text={tGeneral(
            !showFilter || items.length == 0
              ? "noSelectionItemsFound"
              : "noSelectionItemsForSearch",
          )}
        />
      </Box>
      <RowSeparator />
    </Column>
  )

  return (
    <Overlay zIndex={200} centerContent={Platform.OS == "web"}>
      <Card expand style={cardStyle}>
        <Column padTop="XS" gap="XS" padBottom={showFilter ? "none" : "S"}>
          {titleComponent}

          {showFilter && (
            <Box style={styles.itemContainer}>
              <MobileSearchBar
                value={searchString}
                placeholder={tGeneral("searchPlaceholder")}
                onChange={setSearchString}
              />
            </Box>
          )}
        </Column>

        <Column expand gap="XS">
          <PerformanceList
            showFooterSeparator
            showHeaderSeparator
            itemKey={itemKey}
            items={itemsToShow}
            itemDimension={ITEM_HEIGHT}
            renderItem={SelectionItem}
            renderListEmptyComponent={emptyListText}
          />

          <SelectionButtonBar />
        </Column>
      </Card>
    </Overlay>
  )
}

function AggregateSelectionIcon<T>({
  selection,
  itemsToShow,
}: {
  selection: T[]
  itemsToShow: T[]
}) {
  const { onMultiSelection } = useSelectionInternal()

  const isFullSelection = notIn(itemsToShow, selection).length == 0

  const onPress = () => {
    const itemsToSelectionChange = !isFullSelection
      ? itemsToShow.filter(item => !contains(selection, item))
      : itemsToShow

    for (const item of itemsToSelectionChange) onMultiSelection(item)
  }

  return (
    <Icon
      color="primary"
      name={isFullSelection ? "collapse-all" : "expand-all"}
      onPress={onPress}
    />
  )
}

function SelectionItem<T>(item: T) {
  const { singleSelectionInProgress } = useSelectionInternal()

  return singleSelectionInProgress ? (
    <SingleSelectionItem item={item} />
  ) : (
    <MultiSelectionItem item={item} />
  )
}

function SharedSelectionItem<T>({
  item,
  icon,
  onSelection,
}: {
  item: T
  icon: MCIcon
  onSelection: (item: T) => void
}) {
  const { selectionConfig } = useSelectionInternal()

  const displayValue = selectionConfig!.displayMapping(item)

  const onPress = () => onSelection(item)

  return (
    <Pressable onPress={onPress}>
      <Row
        alignCenter
        gap="XXS"
        padHorizontal="XXXS"
        style={styles.itemContainer}
      >
        <Icon name={icon} />
        {typeof displayValue == "string" ? (
          <Label text={displayValue} />
        ) : (
          displayValue
        )}
      </Row>
    </Pressable>
  )
}

function SingleSelectionItem<T>({ item }: { item: T }) {
  const { selectionConfig, onSingleSelection } = useSelectionInternal()

  const config = selectionConfig! as SingleSelectionConfig<T>
  const isSelected = config.selection && isEqual(config.selection, item)
  const icon = isSelected
    ? "checkbox-marked-circle-outline"
    : "checkbox-blank-circle-outline"

  return (
    <SharedSelectionItem
      item={item}
      icon={icon}
      onSelection={onSingleSelection}
    />
  )
}

function MultiSelectionItem<T>({ item }: { item: T }) {
  const { selectionConfig, onMultiSelection } = useSelectionInternal()

  const config = selectionConfig! as MultiSelectionConfig<T>
  const isSelected = contains(config.selection, item)
  const icon = isSelected ? "checkbox-marked-outline" : "checkbox-blank-outline"

  return (
    <SharedSelectionItem
      item={item}
      icon={icon}
      onSelection={onMultiSelection}
    />
  )
}

function SelectionButtonBar() {
  const { tGeneral } = useGeneralTranslation()
  const { selectionConfig, onConfirmSelection, onCancelSelection } =
    useSelectionInternal()

  const config = selectionConfig!
  const isConfirmButtonVisible =
    !("mustConfirm" in config) || config.mustConfirm

  const actionButtons = isConfirmButtonVisible
    ? [
        {
          label: tGeneral("confirm"),
          disabled: isConfirmButtonVisible && !config.selection,
          onPress: () => onConfirmSelection(config.selection),
        },
      ]
    : undefined

  const cancelButton =
    config.preventCancel != true
      ? { label: tGeneral("close"), onPress: onCancelSelection }
      : undefined

  return (
    <ButtonBar
      cancelButton={cancelButton}
      actionButtons={actionButtons}
      contentContainer={{ padLeft: "S", padBottom: "XS" }}
    />
  )
}

function useSelection() {
  const { current: key } = useRef<string>(createId())
  const { selectionInProgress, selectSingle, selectMulti } =
    useSelectionInternal()

  return { key, selectionInProgress, selectSingle, selectMulti }
}

const [SelectionContextProvider, useSelectionInternal] = createCustomContext(
  () => {
    const initialSelectionRef = useRef<any>(undefined)
    const [selectionConfig, setSelectionConfig] =
      useState<AggregatedSelectionConfig<any>>(undefined)
    const [searchString, setSearchString] = useState<string>("")

    const selectionInProgress = selectionConfig && selectionConfig.resolve
    const singleSelectionInProgress =
      selectionInProgress && "mustConfirm" in selectionConfig

    // Exposed selection trigger and update functions

    function prepareForSelection<T>(selection: T | T[]) {
      Keyboard.dismiss()
      initialSelectionRef.current = cloneDeep(selection)
    }
    async function selectSingle<T>(
      config: SingleSelectionConfig<T>,
    ): Promise<T> {
      prepareForSelection(config.selection)

      return new Promise<T>(resolve => {
        setSelectionConfig({
          mustConfirm: config.mustConfirm,
          ...config,
          resolve,
        })
      })
    }
    async function selectMulti<T>(
      config: MultiSelectionConfig<T>,
    ): Promise<T[]> {
      prepareForSelection(config.selection)

      return new Promise<T[]>(resolve => {
        setSelectionConfig({ ...config, resolve })
      })
    }
    function sanitizeSelection<T>(items: T[], selection?: T | T[]) {
      let sanitizedSelection = selection

      if (sanitizedSelection) {
        const isItemsPresent = items.length > 0

        if (isArray(sanitizedSelection))
          sanitizedSelection = isItemsPresent
            ? sanitizedSelection.filter(item => contains(items, item))
            : []
        else if (!isItemsPresent || !contains(items, sanitizedSelection))
          sanitizedSelection = undefined
      }

      return sanitizedSelection
    }
    function updateSelectionConfig<T>(config: AggregatedSelectionConfig<T>) {
      if (
        !config ||
        !selectionInProgress ||
        config?.key != selectionConfig?.key
      )
        return

      initialSelectionRef.current = sanitizeSelection(
        config.items,
        initialSelectionRef.current,
      )

      setSelectionConfig(prevConfig => ({
        ...prevConfig,
        ...config,
        selection: sanitizeSelection(config.items, prevConfig?.selection),
      }))
    }

    // Internal item selection functions

    function onSingleSelection<T>(selection: T) {
      if (!singleSelectionInProgress) return

      setSelectionConfig(
        prevConfig =>
          ({ ...prevConfig, selection }) as SingleSelectionConfig<T>,
      )

      if (selectionConfig.mustConfirm) return

      onConfirmSelection(selection)
    }
    function onMultiSelection<T>(item: T) {
      if (!selectionInProgress) return

      setSelectionConfig(prevConfig => {
        const selection = prevConfig?.selection ?? []

        if (!contains(selection, item)) {
          selection.push(item)
        } else {
          const index = indexOf(selection, item)
          if (index > -1) selection.splice(index, 1)
        }

        return { ...prevConfig, selection } as MultiSelectionConfig<T>
      })
    }

    // Internal final action functions

    function resetSelection() {
      setSearchString("")
      setSelectionConfig(undefined)
      initialSelectionRef.current = undefined
    }
    function resolveSelection<T>(selection: T | T[]) {
      if (!selectionInProgress) return

      if ("mustConfirm" in selectionConfig) {
        selectionConfig.resolve!(selection)
        resetSelection()
        return
      }

      if (!isArray(selection)) return

      selectionConfig.resolve!(selection)
      resetSelection()
    }
    function onConfirmSelection<T>(selection: T | T[]) {
      resolveSelection(selection)
    }
    function onCancelSelection() {
      if (selectionConfig?.preventCancel) return

      resolveSelection(initialSelectionRef.current)
    }

    return {
      selectionConfig,
      selectionInProgress,
      singleSelectionInProgress,

      searchString,
      setSearchString,

      selectSingle,
      selectMulti,
      updateSelectionConfig,

      onSingleSelection,
      onMultiSelection,

      onConfirmSelection,
      onCancelSelection,
    }
  },
)

function useUpdateSelectionConfigEffect<T>(
  config: AggregatedSelectionConfig<T>,
) {
  const { updateSelectionConfig } = useSelectionInternal()

  useTransitionEffect(config, (currentConfig, previousConfig) => {
    if (isEqual(currentConfig, previousConfig)) return
    updateSelectionConfig(currentConfig)
  })
}

const ITEM_HEIGHT = 48

const styles = StyleSheet.create({
  webCard: {
    width: "30%",
    minWidth: 350,
    maxHeight: "80%",
    alignSelf: "center",
  },
  itemContainer: { height: ITEM_HEIGHT },
  tabletCard: { width: 400, alignSelf: "center" },
  cardOuterSpacing: { marginBottom: 24, marginHorizontal: 16 },
})
